import { useDispatch } from 'react-redux';

import { Text } from '@opendoor/bricks/core';
import {
  CheckboxGroupInput,
  CheckboxGroupInputProps,
  CheckboxInputProps,
} from '@opendoor/cinderblocks/growth/novo/inputs/CheckboxInput';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';

import { trackAnswerChange } from 'src/components/fields/trackAnswerChange';
import { getErrorMessage } from 'src/components/flowPage/twoColumn/ErrorMessages';
import FollowUps from 'src/components/novo/fields/FollowUps';
import { noneKey } from 'src/helpers/questions';
import { getQuestionKeys } from 'src/lib/flow';
import type { CheckboxGroupQuestion } from 'src/lib/flow/FlowQuestion';
import { checkValidation } from 'src/lib/flow/validation';
import { changeAnswers, SavedAnswer } from 'src/store/sellerInput';

const getKeysToClear = (
  options: CheckboxGroupQuestion['options'],
  newValues: Record<string, boolean>,
) => {
  const keys: Array<string> = [];
  options.forEach((option) => {
    if (!(typeof option.value === 'string' && newValues[option.value]) && option.followUps) {
      keys.push(...getQuestionKeys(option.followUps));
    }
  });
  return keys;
};

type CheckboxGroupProps = {
  values?: Record<string, boolean> | null;
  question: CheckboxGroupQuestion;
  showValidation?: boolean;
};

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  values,
  question,
  showValidation = false,
}) => {
  const dispatch = useDispatch();
  const { label, helperText, key: name, options } = question;

  const showNone = question.showNoneOfTheAbove;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const answer = values;
    const value = event.target.value;
    const isChecked = !!event.target.checked;
    const newSelections = answer ? { ...answer } : {};

    if (value !== noneKey) {
      // user did not click the none option, so make sure it is set to false
      newSelections[noneKey] = false;
    } else if (isChecked) {
      // user clicked and enabled the none option, so clear out existing values
      Object.entries(newSelections).forEach(([key, _]) => {
        newSelections[key] = false;
      });
    }

    // update our clicked item to the new checked state
    newSelections[value] = isChecked;

    const newAnswers: Array<{ key: string; answer: SavedAnswer | null }> = [];

    if (value === '_other_' && isChecked) {
      newAnswers.push({ key: `${name}.other`, answer: '_other_' });
    }

    // clear out any followups that are unselected
    const keys = getKeysToClear(options, newSelections);
    keys.forEach((key) => {
      newAnswers.push({ key, answer: null });
    });

    newAnswers.push({ key: name, answer: newSelections });
    dispatch(changeAnswers(newAnswers));
    trackAnswerChange(name, newSelections);
  };

  const validationResult = checkValidation(question, values);

  const checkboxes: Array<CheckboxInputProps> = (options || [])
    .filter(({ hidden }) => !hidden)
    .map((optionProps) => {
      const v = optionProps.value as string;
      const checked = values ? !!values[v] : false;
      return {
        id: `${name}-${v}`,
        error: showValidation && !validationResult.isValid,
        children: (
          <>
            {optionProps.label}
            <Text size="100" color="warmgrey800">
              {optionProps.description}
            </Text>
          </>
        ),
        additionalChildren:
          checked && optionProps.followUps ? (
            <FollowUps questions={optionProps.followUps} />
          ) : undefined,
        onChange,
        value: v,
        checked,
      };
    });

  if (showNone) {
    checkboxes.push({
      onChange,
      id: `${name}-${noneKey}`,
      name: `${name}-${noneKey}`,
      value: noneKey,
      error: showValidation && !validationResult.isValid,
      checked: values ? !!values[noneKey] : false,
      showCheckbox: false,
      children: 'None of the above',
    });
  }

  if (label) {
    return (
      <Field<CheckboxGroupInputProps>
        label={label}
        helperText={helperText}
        InputComponent={CheckboxGroupInput}
        errorText={getErrorMessage(validationResult)}
        input={{
          id: `checkbox-group-${name}`,
          error: showValidation && !validationResult.isValid,
          analyticsName: `reception-fe-question-${name}`,
          name: name,
          options: checkboxes,
        }}
      />
    );
  }

  return (
    <CheckboxGroupInput
      id={`checkbox-group-${name}`}
      analyticsName={`reception-fe-question-${name}`}
      error={false}
      name={name}
      options={checkboxes}
    />
  );
};

export default CheckboxGroup;
